import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { Identity } from '../lib/identities';
import { PROVIDER_LABEL_KEY } from '../lib/identity-labels';
import { day } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// The person's logins as a table (#466). Presenter: `ConnectedLogins` owns the
// read and the remove. Remove only ASKS (`onRemove`); the route confirms. A row
// the server marks not `removable` (the GitHub anchor) has no Remove at all.

/** The row's name for a screen reader: the org login, else the email, else its id. */
export function identityName(row: Identity): string {
  return row.accountLogin ?? row.email ?? row.id;
}

export function IdentityTable(props: {
  readonly rows: readonly Identity[];
  /** The id a remove is in flight for — its button is held. */
  readonly busyId: string | null;
  readonly onRemove: (row: Identity) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<Identity>[] = [
    {
      key: 'provider',
      header: t('logins.col_provider'),
      rowHeader: true,
      cell: (row) => t(PROVIDER_LABEL_KEY[row.provider]),
    },
    {
      key: 'org',
      header: t('logins.col_org'),
      mono: true,
      cell: (row) => row.accountLogin ?? t('logins.none'),
    },
    {
      key: 'email',
      header: t('logins.col_email'),
      cell: (row) => row.email ?? t('logins.none'),
    },
    { key: 'linked', header: t('logins.col_linked'), cell: (row) => day(row.linkedAt) },
    {
      key: 'actions',
      header: t('logins.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      cell: (row) => (
        <Show when={row.removable}>
          <div class="data-table__actions">
            <Button
              variant="outline"
              size="sm"
              disabled={props.busyId === row.id}
              aria-label={t('logins.remove_label', {
                provider: t(PROVIDER_LABEL_KEY[row.provider]),
                name: identityName(row),
              })}
              onClick={() => props.onRemove(row)}
            >
              {t('logins.remove')}
            </Button>
          </div>
        </Show>
      ),
    },
  ];
  return (
    <DataTable
      label={t('logins.table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
